import { createSharedComposable } from '@vueuse/core'

function _useHeaderLinks() {
  const route = useRoute()
  const { t } = useI18n()
  
  const headerLinks = computed(() => {
    return [
      {
        label: t('nav.solutions'),
        icon: '',
        to: '/solutions',
        search: false,
        active: route.path.startsWith('/solutions'),
        children: [
          {
            label: t('nav.volume'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/solutions/volume'
          },
          {
            label: t('nav.traffic'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/solutions/traffic'
          },
          {
            label: t('nav.recognition'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/solutions/recognition'
          }
        ]
      },
      {
        label: t('nav.products'),
        icon: '',
        to: '/products',
        search: false,
        active: route.path.startsWith('/products'),
        children: [
          {
            label: t('nav.scanner1'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/products/cpl40'
          },
          {
            label: t('nav.scanner2'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/products/cpl80'
          },
          {
            label: t('nav.scanner3'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/products/cpl260'
          },
          {
            label: t('nav.fusion'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/products/cplfu50'
          }
        ]
      },
      {
        label: t('nav.cases'),
        icon: '',
        to: '/blog'
      },
      {
        label: t('nav.enterprise'),
        icon: '',
        to: '/enterprise',
        search: false,
        active: route.path.startsWith('/enterprise'),
        children: [
          {
            label: t('nav.contact'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/enterprise/support'
          },
          {
            label: t('nav.news'),
            description: '',
            icon: 'i-material-symbols-arrow-forward-rounded',
            to: '/video-courses'
          }
        ]
      }
    ]
  })
  return { headerLinks }
}

export const useHeaderLinks = import.meta.client ? createSharedComposable(_useHeaderLinks) : _useHeaderLinks

function _useFooterLinks() {
  const { t } = useI18n()
  
  const footerLinks = [{
    label: t('footer.community.label'),
    children: [{
      label: t('footer.community.nuxters'),
      to: 'https://nuxters.nuxt.com',
      target: '_blank'
    }, {
      label: t('footer.community.team'),
      to: '/team'
    }, {
      label: t('footer.community.designKit'),
      to: '/design-kit'
    }]
  }, {
    label: t('footer.products.label'),
    children: [{
      label: t('footer.products.uiPro'),
      to: 'https://ui.nuxt.com/pro?utm_source=nuxt-website&utm_medium=footer',
      target: '_blank'
    }, {
      label: t('footer.products.studio'),
      to: 'https://content.nuxt.com/studio/?utm_source=nuxt-website&utm_medium=footer',
      target: '_blank'
    }, {
      label: t('footer.products.hub'),
      to: 'https://hub.nuxt.com/?utm_source=nuxt-website&utm_medium=footer',
      target: '_blank'
    }]
  }, {
    label: t('footer.enterprise.label'),
    children: [{
      label: t('footer.enterprise.support'),
      to: '/enterprise/support'
    }, {
      label: t('footer.enterprise.agencies'),
      to: '/enterprise/agencies'
    }, {
      label: t('footer.enterprise.sponsors'),
      to: '/enterprise/sponsors'
    }]
  }]

  return { footerLinks }
}

export const useFooterLinks = import.meta.client ? createSharedComposable(_useFooterLinks) : _useFooterLinks

const _useNavigation = () => {
  const nuxtApp = useNuxtApp()
  const searchTerm = ref<string>('')

  const { headerLinks } = useHeaderLinks()
  const { footerLinks } = useFooterLinks()

  const searchLinks = computed(() => [
    {
      label: 'Ask AI',
      icon: 'i-lucide-wand',
      to: 'javascript:void(0);',
      onSelect: () => nuxtApp.$kapa?.openModal()
    },
    ...headerLinks.value.map((link) => {
      // Remove `/docs` and `/enterprise` links from command palette
      if (link.search === false) {
        return {
          label: link.label,
          icon: link.icon,
          children: link.children
        }
      }
      return link
    }).filter((link): link is NonNullable<typeof link> => Boolean(link)), {
      label: 'Team',
      icon: 'i-lucide-users',
      to: '/team'
    }, {
      label: 'Design Kit',
      icon: 'i-lucide-palette',
      to: '/design-kit'
    }, {
      label: 'Newsletter',
      icon: 'i-lucide-mail',
      to: '/newsletter'
    }])

  type SearchGroup = {
    id: string
    label: string
    icon?: string
    items: Array<{
      id: string
      label: string
      suffix?: string
      icon?: string
      avatar?: {
        src?: string
        ui?: {
          root: string
        }
      }
      to: string
      onSelect?: () => Promise<void>
    }>
  }

  const searchGroups = computed<SearchGroup[]>(() => {
    const aiGroup: SearchGroup = {
      id: 'ask-ai-search',
      label: 'AI',
      icon: 'i-lucide-wand',
      items: []
    }

    const modulesGroup: SearchGroup = {
      id: 'modules-search',
      label: 'Modules',
      items: []
    }

    const hostingGroup: SearchGroup = {
      id: 'hosting-search',
      label: 'Hosting',
      items: []
    }

    const groups = [aiGroup, modulesGroup, hostingGroup]

    if (!searchTerm.value) {
      return groups
    }

    aiGroup.items = [{
      id: `ask-ai-${searchTerm.value}`,
      label: `Ask AI about "${searchTerm.value}"`,
      icon: 'i-lucide-wand',
      to: 'javascript:void(0);',
      onSelect() {
        return nuxtApp.$kapa.openModal(searchTerm.value)
      }
    }]

    const loadModules = async () => {
      const { modules, fetchModulesList } = useModules()
      if (!modules.value.length) {
        await fetchModulesList()
      }

      modulesGroup.items = modules.value
        .filter(module => ['name', 'icon'].map(field => module[field as keyof typeof module]).filter(Boolean).some(value => typeof value === 'string' && value.search(searchTextRegExp(searchTerm.value)) !== -1))
        .map(module => ({
          id: `module-${module.name}`,
          label: module.name,
          suffix: '',
          avatar: {
            src: moduleImage(module.icon),
            ui: {
              root: 'rounded-none bg-transparent'
            }
          },
          to: `/modules/${module.name}`
        }))
    }

    const loadHosting = async () => {
      const { providers, fetchList } = useHostingProviders()
      if (!providers.value.length) {
        await fetchList()
      }

      hostingGroup.items = providers.value
        .filter(hosting => ['title'].map(field => hosting[field as keyof typeof hosting]).filter(Boolean).some(value => typeof value === 'string' && value.search(searchTextRegExp(searchTerm.value)) !== -1))
        .map(hosting => ({
          id: `hosting-${hosting.path}`,
          label: hosting.title,
          suffix: hosting.description,
          icon: hosting.logoIcon,
          avatar: hosting.logoSrc
            ? {
                src: hosting.logoSrc,
                ui: {
                  root: 'rounded-none bg-transparent'
                }
              }
            : undefined,
          to: hosting.path
        }))
    }

    onMounted(() => {
      Promise.all([
        loadModules(),
        loadHosting()
      ]).catch(error => console.error('Error loading search results:', error))
    })

    return groups
  })

  return {
    searchTerm,
    headerLinks,
    footerLinks,
    searchLinks,
    searchGroups
  }
}

export const useNavigation = import.meta.client ? createSharedComposable(_useNavigation) : _useNavigation


